import "../styles/job.css";
import { lazy, Suspense } from "react";

import AIEngineerHero from "./AIEngineerHero";
import AIEngineerJobRoles from "./AIEngineerJobRoles";

/* 🔥 Lazy-load heavy sections to avoid blocking navigation */
const Demand = lazy(() => import("../pages/AIEngineerDemand.js"));
const FeaturesSection = lazy(() => import("../pages/FeaturesSection"));
const ScrollingFeedback = lazy(() => import("../pages/ScrollingFeedback"));

export default function AIEngineerOverview() {
  return (
    <>
      {/* ================= HERO BANNER ================= */}
      <AIEngineerHero />

      {/* ================= MAIN CONTENT ================= */}
      <section className="genai-page">

        {/* PAGE INTRO */}
        <header className="genai-hero">
          <h1>AI Engineer – Build Intelligent Systems</h1>
          <p className="subtitle">
            Learn Artificial Intelligence, Machine Learning & Smart Applications
          </p>
        </header>

        {/* PROGRAM OVERVIEW */}
        <section className="genai-section">
          <p>
            An AI Engineer is a professional who develops intelligent systems
            using Artificial Intelligence technologies. They design and build
            machine learning models that enable computers to learn from data
            and make decisions. AI engineers work with large datasets,
            algorithms, and programming languages to create smart applications.
          </p>

          <p>
            These professionals develop systems such as chatbots,
            recommendation engines, and image or speech recognition tools.
            Their work helps automate complex tasks and improve efficiency
            across industries including healthcare, finance, manufacturing,
            and technology.
          </p>
        </section>

        {/* JOB ROLES */}
        <AIEngineerJobRoles />

        {/* LEARNING OUTCOMES */}
        <section className="genai-section">
          <h2>Key Learning Outcomes</h2>
          <ul className="outcome-list">
            <li>Understanding the fundamentals of Artificial Intelligence and machine learning</li>
            <li>Build and train machine learning models using Python</li>
            <li>Learn data preprocessing, feature engineering, and model evaluation</li>
            <li>Understand Natural Language Processing (NLP) and Computer Vision basics</li>
            <li>Develop AI-powered applications such as chatbots and recommendation systems</li>
            <li>Work with AI frameworks and libraries to build intelligent solutions</li>
          </ul>
        </section>

        {/* WHO SHOULD ATTEND */}
        <section className="genai-section">
          <h2>Who Should Attend</h2>
          <ul className="outcome-list">
            <li>Students and fresh graduates interested in Artificial Intelligence</li>
            <li>Software developers who want to build AI-powered applications</li>
            <li>Data analysts or IT professionals transitioning into AI roles</li>
            <li>Researchers and engineers interested in automation and intelligent systems</li>
            <li>Anyone curious about building smart AI solutions</li>
          </ul>

          <p className="salary-note">
            Basic programming knowledge is helpful but not mandatory to start learning AI.
          </p>
        </section>

        {/* CAREER PATHWAYS */}
        <section className="genai-section">
          <h2>Career Pathways, Job Roles & Salary Range in India</h2>

          <div className="salary-table-wrapper">
            <table className="salary-table">
              <thead>
                <tr>
                  <th>Job Role</th>
                  <th>*Entry Salary</th>
                  <th>*Mid-Level Salary</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>AI Engineer</td>
                  <td>₹5 – 10 LPA</td>
                  <td>₹10 – 20 LPA</td>
                </tr>
                <tr>
                  <td>Machine Learning Engineer</td>
                  <td>₹6 – 12 LPA</td>
                  <td>₹12 – 24 LPA</td>
                </tr>
                <tr>
                  <td>Data Scientist</td>
                  <td>₹6 – 12 LPA</td>
                  <td>₹12 – 25 LPA</td>
                </tr>
                <tr>
                  <td>NLP Engineer</td>
                  <td>₹6 – 11 LPA</td>
                  <td>₹12 – 22 LPA</td>
                </tr>
                <tr>
                  <td>Computer Vision Engineer</td>
                  <td>₹6 – 12 LPA</td>
                  <td>₹12 – 24 LPA</td>
                </tr>
                <tr>
                  <td>AI Research Engineer</td>
                  <td>₹7 – 14 LPA</td>
                  <td>₹15 – 30+ LPA</td>
                </tr>
              </tbody>
            </table>

            <p className="salary-note">
              *Salary ranges vary based on skills, experience, company, and
              demand in the Artificial Intelligence industry.
            </p>
          </div>
        </section>

        {/* PROJECT HIGHLIGHT */}
        <section className="genai-section highlight">
          <h2>Project Highlights</h2>
          <p>
            <strong>AI Intelligent Chatbot System:</strong> An advanced chatbot
            application built using Artificial Intelligence and machine
            learning techniques. The system processes natural language input,
            retrieves relevant information from datasets, and generates
            accurate responses in real time. It demonstrates skills in
            Natural Language Processing, model integration, and building
            AI-powered conversational applications.
          </p>
        </section>

        {/* ================= LAZY-LOADED SECTIONS ================= */}
        <Suspense fallback={<div style={{ padding: 40 }}>Loading insights…</div>}>
          <Demand />
          <FeaturesSection />
          <ScrollingFeedback />
        </Suspense>

      </section>
    </>
  );
}